import { BellRing, CreditCard, FolderOpen, Info, Mail, Package, Receipt, Settings, SlidersHorizontal, Smartphone, Upload, UserPlus, Users } from 'lucide-vue-next'
import { ChartNoAxesColumn } from 'lucide-vue-next'
import AdminPanelSettings from '~icons/material-symbols/admin-panel-settings'
import { useI18n } from '../composables/useI18n'
import { useAuthStore } from '../stores/auth'
import { useFeatureStore } from '../stores/feature'

/**
 * 侧边栏导航项：一级入口或二级子项。
 * - 一级直接项：提供 `path`，点击直接跳转。
 * - 一级分组项：提供 `items`，点击进入其第一个子项，并在二级列展示 `items`。
 * - 底部设置入口：`footer: true`，不在一级竖条渲染，由底部「设置」按钮展开。
 * - `label` 为完整文案（tooltip / 二级列）；`shortLabel` 为一级竖条专用短文案，缺省回退 `label`。
 */
export interface NavItem {
  key: string
  label: string
  shortLabel?: string
  icon?: any
  path?: string
  items?: NavItem[]
  footer?: boolean
  /** 仅管理员可见（普通用户从菜单中过滤掉） */
  adminOnly?: boolean
}

export interface SettingTabDef {
  key: string
  /** i18n key，渲染时通过 t() 解析 */
  labelKey: string
  icon?: any
  /** 仅管理员可见（非管理员从设置 Tab 中过滤掉） */
  adminOnly?: boolean
}

/** 构造底部「设置」入口：二级项统一为 `<prefix>/setting/<key>`；adminOnly=true 时仅管理员可见 */
export const useSettingEntry = (
  prefix: string,
  tabs: SettingTabDef[],
  adminOnly = false,
): NavItem => {
  const { t } = useI18n()
  const auth = useAuthStore()
  return {
    key: 'setting',
    label: t('nav.setting'),
    shortLabel: t('nav.setting'),
    footer: true,
    adminOnly,
    items: tabs.filter((tab) => !tab.adminOnly || auth.isAdmin).map((tab) => ({
      key: `setting-${tab.key}`,
      label: t(tab.labelKey),
      icon: tab.icon,
      path: `${prefix}/setting/${tab.key}`,
    })),
  }
}

/** 文件管理项（所有模块的底部「设置」共用） */
const SETTING_TAB_FILE: SettingTabDef = { key: 'file', labelKey: 'nav.setting.file', icon: FolderOpen, adminOnly: true }

/** 邮件设置项（所有模块的底部「设置」共用） */
const SETTING_TAB_SMTP: SettingTabDef = { key: 'smtp', labelKey: 'nav.setting.smtp', icon: Mail }

/** 短信设置项（所有模块的底部「设置」共用） */
const SETTING_TAB_SMS: SettingTabDef = { key: 'sms', labelKey: 'nav.setting.sms', icon: Smartphone }

/** 通用设置项：基本设置 + 邮件设置 + 短信设置 + 关于 */
export const SETTING_TABS_BASIC: SettingTabDef[] = [
  { key: 'basic', labelKey: 'nav.setting.basic', icon: Settings },
  SETTING_TAB_FILE,
  SETTING_TAB_SMTP,
  SETTING_TAB_SMS,
  { key: 'about', labelKey: 'nav.setting.about', icon: Info },
]

/** 含文件上传的设置项：基本设置 + 文件上传 + 邮件设置 + 短信设置 + 关于 */
export const SETTING_TABS_UPLOAD: SettingTabDef[] = [
  { key: 'basic', labelKey: 'nav.setting.basic', icon: Settings },
  { key: 'upload', labelKey: 'nav.setting.upload', icon: Upload },
  SETTING_TAB_FILE,
  SETTING_TAB_SMTP,
  SETTING_TAB_SMS,
  { key: 'about', labelKey: 'nav.setting.about', icon: Info },
]

/** 单用户版设置项：基本设置 + 邮件设置 + 短信设置 + 关于 */
export const SETTING_TABS_SINGLE: SettingTabDef[] = [
  { key: 'basic', labelKey: 'nav.setting.basic', icon: Settings },
  SETTING_TAB_FILE,
  SETTING_TAB_SMTP,
  SETTING_TAB_SMS,
  { key: 'about', labelKey: 'nav.setting.about', icon: Info },
]

/** 单用户版含文件上传的设置项：基本设置 + 文件上传 + 邮件设置 + 短信设置 + 关于 */
export const SETTING_TABS_SINGLE_UPLOAD: SettingTabDef[] = [
  { key: 'basic', labelKey: 'nav.setting.basic', icon: Settings },
  { key: 'upload', labelKey: 'nav.setting.upload', icon: Upload },
  SETTING_TAB_FILE,
  SETTING_TAB_SMTP,
  SETTING_TAB_SMS,
  { key: 'about', labelKey: 'nav.setting.about', icon: Info },
]

export interface AdminEntryOptions {
  /** 该模块仅管理员可见的入口（监控 / 日志 / 渠道 / 配置等），渲染在「用户管理」之前 */
  items?: NavItem[]
  /** 用户管理页路径（默认 base 通用页 /base/user） */
  userPath?: string
  /** 是否包含点数充值相关管理项（点数套餐 / 点数购买记录 / 支付设置） */
  withPay?: boolean
}

/**
 * 构造一级「管理」入口：仅管理员可见，且每个模块固定存在。
 * 二级项 = 该模块管理员入口 + 用户管理 + 功能设置 + 注册登录（含注册设置 / 协议设置）。
 * 点数套餐 / 点数购买记录仅在「开启点数」时显示；用户点数并入用户管理页。
 * 邮件设置统一收进底部「设置」的邮件 Tab。
 */
export const useAdminEntry = (opts: AdminEntryOptions = {}): NavItem => {
  const { t } = useI18n()
  const feature = useFeatureStore()
  const payItems: NavItem[] = []
  if (opts.withPay) {
    if (feature.creditEnabled) {
      payItems.push(
        { key: 'admin-package', label: t('nav.admin.package'), icon: Package, path: '/base/package' },
        { key: 'admin-order', label: t('nav.admin.order'), icon: Receipt, path: '/base/order' },
      )
    }
    payItems.push({ key: 'admin-pay', label: t('nav.admin.pay'), icon: CreditCard, path: '/base/pay' })
  }
  return {
    key: 'admin',
    label: t('nav.admin'),
    shortLabel: t('nav.admin.short'),
    icon: AdminPanelSettings,
    adminOnly: true,
    items: [
      ...(opts.items ?? []),
      ...payItems,
      { key: 'admin-notify', label: t('nav.admin.notify'), icon: BellRing, path: '/base/notify' },
      { key: 'admin-attr', label: t('nav.admin.attr'), icon: ChartNoAxesColumn, path: '/base/attr' },
      { key: 'admin-user', label: t('nav.admin.user'), icon: Users, path: opts.userPath || '/base/user' },
      { key: 'admin-setting', label: t('nav.admin.setting'), icon: SlidersHorizontal, path: '/base/setting' },
      { key: 'admin-register', label: t('nav.admin.register'), icon: UserPlus, path: '/base/register' },
    ],
  }
}
